import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Menu as MenuIcon, X, Phone } from "lucide-react";
import { SITE, scrollToId } from "@/lib/site";

const LINKS = [
  { label: "Story", id: "story" },
  { label: "Menu", id: "menu" },
  { label: "Gallery", id: "gallery" },
  { label: "Reviews", id: "reviews" },
  { label: "Visit", id: "visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-obsidian/80 backdrop-blur-xl border-b border-terracotta/15"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <button
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
            className="flex items-center gap-3 group"
            data-testid="nav-brand"
          >
            <span className="w-9 h-9 rounded-full bg-terracotta flex items-center justify-center transition-transform duration-500 group-hover:rotate-180">
              <Sun className="w-5 h-5 text-obsidian" strokeWidth={2.2} />
            </span>
            <span className="font-serif-d text-2xl font-semibold tracking-tight text-cream">
              Alejandra<span className="text-terracotta">'s</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-9" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="font-mono-d text-[11px] uppercase tracking-[0.25em] text-cream/60 hover:text-terracotta transition-colors duration-300"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <a
              href={SITE.phoneHref}
              className="font-mono-d text-[11px] tracking-[0.15em] text-cream/60 hover:text-cream transition-colors duration-300 flex items-center gap-2"
              data-testid="nav-phone"
            >
              <Phone className="w-3.5 h-3.5" /> {SITE.phone}
            </a>
            <button
              onClick={() => go("reserve")}
              className="px-6 py-2.5 rounded-full bg-terracotta text-obsidian font-sans-d text-sm font-semibold hover:bg-terracotta-hover transition-colors duration-300"
              data-testid="nav-reserve-btn"
            >
              Reserve a Table
            </button>
          </div>

          <button
            className="md:hidden text-cream p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            aria-label="Open menu"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-obsidian/95 backdrop-blur-2xl flex flex-col"
            data-testid="nav-mobile-drawer"
          >
            <div className="flex justify-end p-6">
              <button
                onClick={() => setOpen(false)}
                className="text-cream p-2"
                data-testid="nav-mobile-close"
                aria-label="Close menu"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-start justify-center px-10 gap-2">
              {[...LINKS, { label: "Reserve", id: "reserve" }].map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="font-serif-d text-5xl font-semibold text-cream hover:text-terracotta transition-colors duration-300 py-2"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.a
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                href={SITE.phoneHref}
                className="mt-8 font-mono-d text-sm tracking-[0.2em] text-terracotta"
                data-testid="nav-mobile-phone"
              >
                {SITE.phone}
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
